/**
 * Hook pour la dictée vocale via Web Speech API
 *
 * FONCTIONNALITÉ BONUS - priorité secondaire
 * Si Web Speech API non disponible, retourne isSupported: false
 * Son absence n'est PAS un bug
 *
 * Navigateurs supportés: Chrome, Edge, Safari (partiel)
 * Non supporté: Firefox
 */

import { useState, useCallback, useEffect, useRef } from 'react';

// Types pour Web Speech API (non inclus dans les types TS standard)
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

// Déclaration globale pour les constructeurs Web Speech API
declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition;
    webkitSpeechRecognition?: new () => SpeechRecognition;
  }
}

export interface UseSpeechRecognitionResult {
  /** Indique si Web Speech API est supporté par le navigateur */
  isSupported: boolean;
  /** Indique si l'écoute est en cours */
  isListening: boolean;
  /** Texte transcrit (résultat final) */
  transcript: string;
  /** Texte en cours de transcription (résultat intermédiaire) */
  interimTranscript: string;
  /** Erreur éventuelle */
  error: string | null;
  /** Démarre l'écoute */
  startListening: () => void;
  /** Arrête l'écoute */
  stopListening: () => void;
  /** Réinitialise le transcript et l'erreur */
  reset: () => void;
}

/**
 * Hook pour la reconnaissance vocale
 * @param onTranscript - Callback appelé avec le texte transcrit (facultatif)
 * @returns État et contrôles de la reconnaissance vocale
 */
export function useSpeechRecognition(
  onTranscript?: (text: string) => void
): UseSpeechRecognitionResult {
  // Vérifier si Web Speech API est supporté
  const SpeechRecognitionAPI =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const isSupported = Boolean(SpeechRecognitionAPI);

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Initialiser la reconnaissance vocale
  useEffect(() => {
    if (!isSupported || !SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR'; // Français par défaut

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setTranscript((prev) => prev + finalTranscript);
        if (onTranscript) {
          onTranscript(finalTranscript);
        }
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);

      // Mapper les erreurs à des messages utilisateur
      switch (event.error) {
        case 'not-allowed':
        case 'permission-denied':
          setError('Accès au microphone refusé');
          break;
        case 'no-speech':
          // Pas une vraie erreur, juste pas de parole détectée
          setError(null);
          break;
        case 'audio-capture':
          setError('Aucun microphone détecté');
          break;
        case 'network':
          setError('Erreur réseau');
          break;
        case 'aborted':
          // Arrêt volontaire, pas une erreur
          setError(null);
          break;
        default:
          setError('Erreur de reconnaissance vocale');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [isSupported, SpeechRecognitionAPI, onTranscript]);

  const startListening = useCallback(() => {
    if (!isSupported || !recognitionRef.current) {
      return;
    }

    setError(null);

    try {
      recognitionRef.current.start();
    } catch (e) {
      // Peut échouer si déjà en cours d'écoute
      console.warn('[SpeechRecognition] Start failed:', e);
    }
  }, [isSupported]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
    } catch (e) {
      console.warn('[SpeechRecognition] Stop failed:', e);
    }
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  return {
    isSupported,
    isListening,
    transcript,
    interimTranscript,
    error,
    startListening,
    stopListening,
    reset,
  };
}

export default useSpeechRecognition;
